/** What the browser can tell about a video or audio file before uploading it. */
export type Probe = {
  durationSeconds?: number;
  width?: number;
  height?: number;
  /** A video's poster frame, as a JPEG. */
  poster?: Blob;
};

const TIMEOUT_MS = 8000;
const POSTER_MAX_PX = 720;

/**
 * Reads a video's or audio file's length (and a video's size and a poster
 * frame about a second in). Best effort: anything the browser can't decode
 * in time resolves to what it managed, possibly nothing.
 */
export function probeMedia(file: File): Promise<Probe> {
  const video = file.type.startsWith('video/');
  if (!video && !file.type.startsWith('audio/')) return Promise.resolve({});
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const element = document.createElement(video ? 'video' : 'audio');
    const result: Probe = {};
    const done = () => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      element.removeAttribute('src');
      resolve(result);
    };
    const timer = setTimeout(done, TIMEOUT_MS);
    element.preload = 'metadata';
    element.muted = true;
    element.onerror = done;
    element.onloadedmetadata = () => {
      if (Number.isFinite(element.duration)) {
        result.durationSeconds = Math.round(element.duration);
      }
      if (!(element instanceof HTMLVideoElement)) return done();
      result.width = element.videoWidth || undefined;
      result.height = element.videoHeight || undefined;
      element.currentTime = Math.min(1, element.duration / 2 || 0);
    };
    element.onseeked = () => {
      if (!(element instanceof HTMLVideoElement)) return done();
      const scale = Math.min(
        1,
        POSTER_MAX_PX / Math.max(element.videoWidth, element.videoHeight),
      );
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(element.videoWidth * scale);
      canvas.height = Math.round(element.videoHeight * scale);
      const context = canvas.getContext('2d');
      if (!context) return done();
      context.drawImage(element, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (blob) result.poster = blob;
          done();
        },
        'image/jpeg',
        0.82,
      );
    };
    element.src = url;
  });
}
